import React, { useEffect, useRef, useState } from 'react';
import * as ReactDOMServer from 'react-dom/server';
import cx from 'classnames';

import Highcharts, {
  AxisTypeValue,
  Options,
  Point,
  SeriesLineOptions,
  SeriesColumnOptions,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts';

import { ChartTooltip } from './ChartTooltip';
import { ChartLoader } from './ChartLoader';

import HighchartsReact from 'highcharts-react-official';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import highchartsExportData from 'highcharts/modules/export-data';
import highchartsExport from 'highcharts/modules/exporting';
import highchartsNoData from 'highcharts/modules/no-data-to-display';
import styles from './Chart.module.scss';

import { removeExcessMarkers } from '../utils/chartUtils';

// Wait until Highcharts is completely loaded to init extension modules in order to avoid errors.
if (typeof Highcharts === 'object') {
  highchartsAccessibility(Highcharts);
  highchartsExport(Highcharts);
  highchartsExportData(Highcharts);
  highchartsNoData(Highcharts);
}

export interface ChartType {
  /** Type of chart: currently only line and column (bar) are supported. */
  type: 'line' | 'column';
}

export interface ChartComponentProps {
  /** Classname for the Chart component container div */
  className?: Parameters<typeof cx>[0];
  /** The data used to draw the chart */
  data: {
    start?: string;
    end?: string;
    series: SeriesLineOptions[] | SeriesColumnOptions[];
    xAxis?: XAxisOptions;
    yAxis?: YAxisOptions;
  };
  /** Remove clickability of legend for non-interactive use-cases */
  disableLegendClick?: boolean;
  /** Hide point markers on line series charts */
  hideMarkers?: boolean;
  /** Loading state of chart, determines when loader shows */
  isLoading?: boolean;
  /** Message to display when there's no data to show for a given chart configuration */
  noDataMessage?: string;
  /** Interval between points on the x-axis of timeseries charts */
  pointInterval?: 'hour' | 'day' | 'week' | 'month';
  /** Function to pass chart context to parent for use with filter controls -- only used with ChartGroup */
  setChart?: (arg0: any) => void;
  /** Amount of time displayed in the chart dataset. */
  timeRange?: 'day' | 'week' | 'month' | 'year';
  /** Custom tooltip component to use with chart if desired. Gets passed the tooltip context. */
  tooltipComponent?: React.ReactElement;
  /** Type of x-axis, e.g. 'linear' or 'datetime' */
  xAxisType?: Exclude<AxisTypeValue, 'logarithmic' | 'treegrid'>;
}

export interface ChartConfigProps {
  /** Optional custom label for the x-axis */
  xAxisLabel?: string;
  /** Optional custom label for the y-axis */
  yAxisLabel?: string;
}

export type ChartProps = ChartConfigProps & ChartComponentProps;

const pointIntervals = {
  hour: 60 * 60 * 1000, // 1 hour in milliseconds
  day: 24 * 60 * 60 * 1000, // 1 day
  month: 30 * 24 * 60 * 60 * 1000, // 1 month
};

export const Chart: React.FC<ChartType & ChartProps> = ({
  className,
  data,
  disableLegendClick,
  hideMarkers = false,
  isLoading,
  noDataMessage,
  pointInterval = 'day',
  setChart,
  timeRange,
  tooltipComponent,
  type: chartType,
  xAxisLabel,
  xAxisType = 'datetime',
  yAxisLabel,
}) => {
  Highcharts.SVGRenderer.prototype.symbols.overflow = () => [
    'M2 1C2 1.55228 1.55228 2 1 2C0.447715 2 0 1.55228 0 1C0 0.447715 0.447715 0 1 0C1.55228 0 2 0.447715 2 1Z',
    'M7 2C7.55228 2 8 1.55228 8 1C8 0.447715 7.55228 0 7 0C6.44772 0 6 0.447715 6 1C6 1.55228 6.44772 2 7 2Z',
    'M13 2C13.5523 2 14 1.55228 14 1C14 0.447715 13.5523 0 13 0C12.4477 0 12 0.447715 12 1C12 1.55228 12.4477 2 13 2Z',
  ];

  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const isYearByMonth = timeRange === 'year' && pointInterval === 'month';

  let dateFormatString = '';

  switch (pointInterval) {
    case 'hour':
      if (timeRange === 'day') {
        dateFormatString = '%l%P';
      } else {
        dateFormatString = '%A, %B %e, %l%P';
      }
      break;
    case 'day':
    case 'week':
      dateFormatString = '%A, %B %e';
      break;
    case 'month':
      dateFormatString = '%B %Y';
      break;
  }

  const [chartOptions, setChartOptions] = useState<Options>({
    // https://api.highcharts.com/highcharts/accessibility
    accessibility: {
      enabled: true,
    },
    // https://api.highcharts.com/highcharts/chart
    chart: {
      marginLeft: 60,
      marginTop: 80,
      spacingLeft: 0,
      styledMode: true,
      type: chartType,
    },
    // https://api.highcharts.com/highcharts/credits
    credits: {
      enabled: false,
    },
    // https://api.highcharts.com/highcharts/exporting
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'viewData',
            'downloadCSV',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
          ],
          symbol: 'overflow',
          text: 'Actions',
        },
      },
      chartOptions: {
        chart: {
          className: 'chart-export',
          marginLeft: 80,
        },
      },
      enabled: true,
      sourceWidth: 800,
    },
    // https://api.highcharts.com/highcharts/legend
    legend: {
      align: 'left',
      padding: 0,
      verticalAlign: 'top',
    },
    // https://api.highcharts.com/highcharts/plotOptions
    plotOptions: {
      column: {
        accessibility: {
          keyboardNavigation: {
            enabled: true,
          },
        },
        groupPadding: 0.2,
      },
      series: {
        accessibility: {
          keyboardNavigation: {
            enabled: true,
          },
        },
      },
    },
    title: {
      align: 'left',
      text: '',
      floating: true,
    },
    tooltip: {
      positioner: (labelWidth: number, labelHeight: number, point: Point) => {
        const chartPadding = 20;
        if (point.plotX === undefined) {
          return { x: 0, y: 0 };
        }
        const { plotWidth, plotLeft } = chartRef.current
          ? chartRef.current['chart']
          : { plotWidth: 0, plotLeft: 0 };
        let xPos = point.plotX + plotLeft + chartPadding;
        if (xPos + plotLeft + labelWidth > plotWidth) {
          xPos = Math.max(
            50,
            point.plotX + plotLeft - labelWidth - chartPadding,
          );
        }
        return { x: xPos, y: 20 };
      },
      shared: true,
      useHTML: true,
    },
    xAxis: {
      crosshair: true,
      labels: {
        y: 24,
      },
      tickLength: 8,
    },
    ...data,
  });

  useEffect(() => {
    setChart && chartRef.current && setChart(chartRef.current.chart);
  }, [chartRef, setChart]);

  useEffect(() => {
    // lang dynamic options
    setChartOptions(prevChartOptions => {
      return {
        ...prevChartOptions,
        lang: {
          noData:
            noDataMessage ||
            'No data to display. Check your time range and/or filter settings and try again.',
        },
      };
    });
    if (chartRef.current) {
      // @ts-ignore Turn off default 'no data' messaging in favor of our own
      chartRef.current.chart.hideNoData();
    }
  }, [noDataMessage]);

  useEffect(() => {
    // xAxis dynamic options
    setChartOptions((prevChartOptions: Options) => {
      const prevXAxisOptions = prevChartOptions.xAxis || {};
      return {
        ...prevChartOptions,
        xAxis: {
          ...prevXAxisOptions,
          dateTimeLabelFormats: {
            hour: '%l%P',
            day: timeRange === 'week' ? '%a' : '%e',
            week: '%b %e',
            month: '%b',
          },
          tickInterval:
            xAxisType === 'datetime'
              ? isYearByMonth
                ? undefined
                : pointIntervals[pointInterval]
              : 1,
          title: {
            margin: 16,
            text:
              xAxisLabel ||
              (timeRange &&
                `${timeRange[0].toUpperCase()}${timeRange?.slice(1)}`) ||
              'Date',
          },
          type: xAxisType
            ? xAxisType
            : chartType === 'line'
            ? 'linear'
            : 'category',
        },
      };
    });
  }, [
    timeRange,
    xAxisLabel,
    xAxisType,
    isYearByMonth,
    pointInterval,
    chartType,
  ]);

  useEffect(() => {
    setChartOptions(prevChartOptions => {
      return {
        ...prevChartOptions,
        yAxis: {
          ...prevChartOptions.yAxis,
          title: {
            text: yAxisLabel || 'Count',
          },
        },
      };
    });
  }, [yAxisLabel]);

  useEffect(() => {
    // legend dynamic options
    setChartOptions(prevChartOptions => {
      return {
        ...prevChartOptions,
        legend: {
          ...prevChartOptions.legend,
          className: `highcharts-no-tooltip${
            disableLegendClick ? ' legend-disabled' : ''
          }`,
        },
      };
    });
  }, [disableLegendClick]);

  useEffect(() => {
    // chart dynamic options
    setChartOptions(prevChartOptions => {
      return {
        ...prevChartOptions,
        chart: {
          ...prevChartOptions.chart,
          type: chartType,
          events: {
            render: () =>
              removeExcessMarkers({ chartRef, chartType, hideMarkers }),
          },
        },
      };
    });
  }, [chartType, hideMarkers, data]);

  useEffect(() => {
    // tooltip dynamic options
    setChartOptions(prevChartOptions => {
      return {
        ...prevChartOptions,
        tooltip: {
          ...prevChartOptions.tooltip,
          formatter: function() {
            // The formatter function is expected to return a string, so we use
            // renderToString to give it what it wants.
            return ReactDOMServer.renderToString(
              <ChartTooltip
                {...this}
                chartType={chartType}
                tooltipComponent={tooltipComponent}
                xAxisType={xAxisType}
                dateFormatString={dateFormatString}
              />,
            );
          },
        },
      };
    });
  }, [chartType, tooltipComponent, xAxisType, dateFormatString]);

  useEffect(() => {
    // plotOptions dynamic options
    if (!data || !data.start) {
      return;
    }
    setChartOptions(prevChartOptions => {
      const prevPlotOptions = prevChartOptions.plotOptions || {};

      return {
        ...prevChartOptions,
        plotOptions: {
          ...prevPlotOptions,
          series: {
            ...prevPlotOptions.series,
            events: {
              legendItemClick: e => {
                if (disableLegendClick) {
                  e.preventDefault();
                }
              },
            },
            marker: {
              enabled: !hideMarkers,
            },
            pointStart:
              xAxisType === 'datetime' && data.start
                ? Date.parse(data.start)
                : 1,
            pointInterval: isYearByMonth
              ? undefined
              : xAxisType === 'datetime'
              ? pointIntervals[pointInterval]
              : 1,
            pointIntervalUnit: pointInterval === 'month' ? 'month' : undefined,
          },
        },
        ...data,
      };
    });
  }, [
    disableLegendClick,
    hideMarkers,
    xAxisType,
    isYearByMonth,
    pointInterval,
    data,
  ]);

  return (
    <div className={cx(styles.chart, className)}>
      {isLoading ? (
        <ChartLoader />
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartRef}
        />
      )}
    </div>
  );
};

Chart.displayName = 'Chart';
export default Chart;
export { styles };
